import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import { FlowType } from "@/store/flowTypes.store";
import { ApiError, handleApiError } from "@/utils/errorHandler";

interface AddFlowTypeFormProps {
	flowType: FlowType | null;
	onClose: () => void;
}

const FLOW_TYPES_QUERY_KEY = ["flow-types"] as const;

const AddFlowTypeForm: React.FC<AddFlowTypeFormProps> = ({
	flowType,
	onClose
}) => {
	const { success, error } = useToast();
	const queryClient = useQueryClient();
	const [formData, setFormData] = useState({
		name: flowType?.name || ""
	});

	const createFlowTypeMutation = useMutation({
		mutationFn: async (data: { name: string }) => {
			const { data: response } = await apiClient.post<FlowType>(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TYPES", "CREATE"]),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: FLOW_TYPES_QUERY_KEY });
			success("Integration created successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create integration");
		}
	});

	const updateFlowTypeMutation = useMutation({
		mutationFn: async ({
			id,
			data
		}: {
			id: string;
			data: { name: string };
		}) => {
			const { data: response } = await apiClient.put<FlowType>(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TYPES", "UPDATE_BY_ID"], id),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: FLOW_TYPES_QUERY_KEY });
			success("Integration updated successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update integration");
		}
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.name.trim()) {
			error("Name is required");
			return;
		}

		if (flowType) {
			updateFlowTypeMutation.mutate({
				id: flowType.id,
				data: { name: formData.name.trim() }
			});
		} else {
			createFlowTypeMutation.mutate({ name: formData.name.trim() });
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const isLoading =
		createFlowTypeMutation.isPending || updateFlowTypeMutation.isPending;

	return (
		<div className="h-full">
			<form
				onSubmit={handleSubmit}
				className="bg-white flex flex-col h-full max-w-md mx-auto rounded-lg"
			>
				<div className="mb-4 flex-1">
					<div className="mb-4">
						<label
							htmlFor="name"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Name
						</label>
						<input
							name="name"
							type="text"
							placeholder="Enter flow type name"
							value={formData.name}
							onChange={handleInputChange}
							disabled={isLoading}
							className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>
				</div>

				<div className="py-4 bg-white mb-[15px]">
					<button
						type="submit"
						disabled={isLoading}
						className="w-full bg-blue-fynxt text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
					>
						{isLoading ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{flowType ? "Updating..." : "Creating..."}
							</>
						) : flowType ? (
							"Update Integration"
						) : (
							"Create Integration"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default AddFlowTypeForm;
